import React, { useState, useCallback } from 'react';
import { FormField } from '@/shared/components/ui/FormField';
import { FileDropzone } from '@/shared/components/ui/FileDropzone';
import { api } from '@/lib/api';
import { Icon } from '@/shared/icons';

interface ImageUploaderFieldProps {
  label: string;
  description?: string;
  value: string | string[] | undefined;
  onChange: (value: unknown) => void;
  multiple?: boolean;
}

/** Campo de upload de imagens do customizer — consumidor do FileDropzone shared (F84). */
export const ImageUploaderField: React.FC<ImageUploaderFieldProps> = ({
  label,
  description,
  value,
  onChange,
  multiple = false,
}) => {
  const [uploading, setUploading] = useState(false);

  const images = multiple
    ? (value as string[]) || []
    : value
      ? [value as string]
      : [];

  const uploadFile = useCallback(async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('uploads?folder=websites', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data.data.url as string;
  }, []);

  const handleFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    setUploading(true);
    try {
      if (multiple) {
        const urls: string[] = [];
        for (const file of files) {
          const url = await uploadFile(file);
          urls.push(url);
        }
        onChange([...images, ...urls]);
      } else {
        const url = await uploadFile(files[0]);
        onChange(url);
      }
    } catch {
      // Error handled by API interceptor
    } finally {
      setUploading(false);
    }
  }, [multiple, images, onChange, uploadFile]);

  const removeImage = (index: number) => {
    if (multiple) {
      const updated = images.filter((_, i) => i !== index);
      onChange(updated);
    } else {
      onChange(undefined);
    }
  };

  const showUploadArea = multiple || images.length === 0;
  const hasImages = images.length > 0;

  return (
    <FormField label={label} description={description}>
      <div className="space-y-3">
        {/* Image preview grid */}
        {hasImages && (
          <div className="grid grid-cols-3 gap-2">
            {images.map((url, index) => (
              <div
                key={index}
                className="relative group rounded-lg overflow-hidden border aspect-video"
                style={{ borderColor: 'var(--theme-card-border)' }}
              >
                <img
                  src={url}
                  alt={`${label} ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: '#ffffff',
                  }}
                  aria-label="Remover imagem"
                  data-testid={`image-uploader-remove-${index}`}
                >
                  <Icon name="x" className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Área de upload — FileDropzone shared (F84) */}
        {showUploadArea && (
          <FileDropzone
            accept="image/*"
            multiple={multiple}
            uploading={uploading}
            compact={hasImages}
            icon={hasImages ? 'upload' : 'image'}
            label={hasImages ? 'Adicionar mais imagens' : 'Clique ou arraste imagens aqui'}
            hint="JPG, PNG, WebP, GIF"
            onFiles={(files) => { void handleFiles(files); }}
            data-testid="image-uploader-dropzone"
          />
        )}
      </div>
    </FormField>
  );
};
